// src/components/MentoringJournal.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from '../axiosInstance';
import { useTranslation } from 'react-i18next';
import { usePairing } from '../context/PairingContext';

// Hilfsfunktion für Mood-Keys wie moodHappy
function capitalize(str) {
  if (!str || typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function MentoringJournal({ user, viewerRole, mentor }) {
  const { t } = useTranslation();
  const { selectedMatchingId } = usePairing();

  const [entry, setEntry] = useState('');
  const [mood, setMood] = useState('neutral');
  const [isPublic, setIsPublic] = useState(false);
  const [entries, setEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  const [error, setError] = useState('');

  // Fallback-UserId für Ownership-Vergleich, falls Flags nicht mitkommen
  const currentUserId = user?.user_id ?? null;

  const loadEntries = useCallback(async () => {
    setError('');
    if (!selectedMatchingId) {
      setEntries([]);
      return;
    }
    try {
      const res = await axios.get('/mentoring/journal', {
        params: { matching_id: selectedMatchingId },
        withCredentials: true,
      });
      setEntries(res.data);
    } catch (err) {
      setError(t('mentoringJournal.loadError') || 'Fehler beim Laden der Einträge.');
    }
  }, [selectedMatchingId, t]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const handleSubmit = async () => {
    if (!entry.trim()) return;
    if (!selectedMatchingId) {
      setError(t('mentoringJournal.noPairingSelected') || 'Kein Pairing ausgewählt.');
      return;
    }

    try {
      const res = await axios.post(
        '/mentoring/journal',
        {
          text: entry,
          mood,
          is_public: isPublic,
          matching_id: selectedMatchingId,
        },
        { withCredentials: true }
      );

      // *** Anpassung: neuen Eintrag sofort mit Owner-Flags & user_id anreichern,
      // damit Bearbeiten/Löschen ohne Reload sichtbar sind ***
      const ownerId = user?.user_id ?? user?.id ?? null;
      const created = res.data || {};
      const hydrated = {
        ...created,
        user_id: created.user_id ?? ownerId,
        is_owner: true,
        isOwner: true,
      };

      setEntries([hydrated, ...entries]);
      setEntry('');
      setMood('neutral');
      setIsPublic(false);
      setError('');
    } catch (err) {
      setError(t('mentoringJournal.saveError') || 'Fehler beim Speichern.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('mentoringJournal.deleteConfirm') || 'Eintrag wirklich löschen?')) return;
    try {
      await axios.delete(`/mentoring/journal/${id}`, { withCredentials: true });
      setEntries(entries.filter((e) => e.id !== id));
      setError('');
    } catch (err) {
      setError(t('mentoringJournal.deleteError') || 'Fehler beim Löschen.');
    }
  };

  const startEditing = (id, text) => {
    setEditId(id);
    setEditText(text);
  };

  const cancelEditing = () => {
    setEditId(null);
    setEditText('');
  };

  const saveEdit = async () => {
    try {
      const res = await axios.patch(
        `/mentoring/journal/${editId}`,
        { text: editText },
        { withCredentials: true }
      );
      setEntries(entries.map((e) => (e.id === editId ? res.data : e)));
      cancelEditing();
      setError('');
    } catch (err) {
      setError(t('mentoringJournal.editError') || 'Fehler beim Bearbeiten.');
    }
  };

  const gefilterteEintraege = entries.filter((e) =>
    e.text?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => new Date(dateStr).toLocaleString();

  return (
    <div className="bg-gray-800 p-4 rounded-2xl shadow">
      <h2 className="font-bold mb-2">{t('mentoringJournal.heading')}</h2>
      {error && (
        <div className="mb-2 text-red-400 text-sm font-semibold">{error}</div>
      )}

      <textarea
        value={entry}
        onChange={(e) => setEntry(e.target.value)}
        className="w-full p-2 rounded bg-gray-700 text-white"
        placeholder={t('mentoringJournal.entryPlaceholder')}
      />

      <div className="mt-2">
        <label className="mr-2">{t('mentoringJournal.mood')}</label>
        <select
          value={mood}
          onChange={(e) => setMood(e.target.value)}
          className="text-black"
        >
          <option value="happy">{t('mentoringJournal.moodHappy')}</option>
          <option value="neutral">{t('mentoringJournal.moodNeutral')}</option>
          <option value="sad">{t('mentoringJournal.moodSad')}</option>
        </select>
      </div>

      {viewerRole === 'mentee' && (
        <div className="mt-2">
          <label>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            <span className="ml-2">{t('mentoringJournal.allowMentorView')}</span>
          </label>
        </div>
      )}

      <button
        onClick={handleSubmit}
        className="mt-2 bg-mentixxgreen text-black px-4 py-1 rounded"
      >
        {t('mentoringJournal.save')}
      </button>

      <div className="mt-6">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('mentoringJournal.searchPlaceholder')}
          className="w-full p-2 rounded bg-gray-700 text-white"
        />
      </div>

      <div className="mt-4 space-y-2">
        {gefilterteEintraege.map((e) => {
          // Defensive: handle missing/invalid mood values
          const moodKey =
            e.mood && typeof e.mood === 'string'
              ? `mentoringJournal.mood${capitalize(e.mood)}`
              : '';
          const moodLabel =
            t(moodKey) !== moodKey
              ? t(moodKey)
              : t('mentoringJournal.moodUnknown');

          // Owner-Erkennung: bevorzugt is_owner/isOwner, Fallback auf user_id-Vergleich
          const isOwner =
            (e?.is_owner ?? e?.isOwner) ??
            (currentUserId != null && e.user_id === currentUserId);

          return (
            <div
              key={e.id}
              className={`bg-gray-700 p-2 rounded text-sm border-l-4 ${
                e.is_public ? 'border-mentixxgreen' : 'border-transparent'
              }`}
            >
              <div className="text-gray-400">
                {formatDate(e.created_at)} – {t('mentoringJournal.mood')}: {moodLabel}
                {e.is_public && (
                  <span className="ml-2 text-mentixxgreen text-xs font-semibold">
                    🔓 {t('mentoringJournal.public')}
                  </span>
                )}
              </div>

              {isOwner && editId === e.id ? (
                <>
                  <textarea
                    value={editText}
                    onChange={(ev) => setEditText(ev.target.value)}
                    className="w-full p-1 rounded bg-gray-600 text-white mt-1"
                  />
                  <div className="flex space-x-2 mt-1">
                    <button
                      onClick={saveEdit}
                      className="bg-mentixxgreen text-black px-2 py-1 rounded"
                    >
                      {t('mentoringJournal.save')}
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="bg-gray-500 text-white px-2 py-1 rounded"
                    >
                      {t('mentoringJournal.cancel')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="whitespace-pre-wrap">{e.text}</div>
                  {isOwner && (
                    <div className="flex space-x-2 mt-1">
                      <button
                        onClick={() => startEditing(e.id, e.text)}
                        className="text-mentixxgreen text-xs underline"
                      >
                        {t('mentoringJournal.edit')}
                      </button>
                      <button
                        onClick={() => handleDelete(e.id)}
                        className="text-red-400 text-xs underline"
                      >
                        {t('mentoringJournal.delete')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MentoringJournal;
